import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import InviteLinkDialog from './InviteLinkDialog';

const Container = styled.div``;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;

  h2 { margin: 0; color: ${(p) => p.theme.colors.text.primary}; }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px 24px;
  font-size: ${(p) => p.theme.metrics.font.size.sm};
  color: ${(p) => p.theme.colors.text.primary};

  b { opacity: 0.6; font-weight: 600; display: block; margin-bottom: 2px; }
`;

export default function VisitDetailsPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { canWrite } = useAuth();

  const [visit, setVisit] = useState(null);
  const [movements, setMovements] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [inviteDialog, setInviteDialog] = useState({ open: false, link: null, smsSent: false });
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    setFetching(true);
    Promise.all([
      api.get(`/v1/visits/${id}`),
      api.get(`/v1/visits/${id}/movements`),
    ])
      .then(([v, m]) => {
        setVisit(v.data?.data || null);
        setMovements(m.data?.data || []);
      })
      .finally(() => setFetching(false));
  }, [id, refreshTrigger]);

  const handleSendInvite = useCallback(async (trySms) => {
    try {
      const res = await api.post(`/v1/visits/${id}/${trySms ? 'resend-invite' : 'regenerate-link'}`);
      const d = res.data?.data;
      setInviteDialog({ open: true, link: d?.inviteLink || null, smsSent: !!d?.smsSent });
      setRefreshTrigger((p) => p + 1);
    } catch { /* hata toast'ı interceptor'da */ }
  }, [id]);

  const handleCancel = useCallback(async () => {
    setBusy(true);
    try {
      await api.post(`/v1/visits/${id}/cancel`);
      setRefreshTrigger((p) => p + 1);
    } catch { /* hata toast'ı interceptor'da */ }
    setCancelConfirm(false);
    setBusy(false);
  }, [id]);

  const fmt = useCallback((d) => (d ? new Date(d).toLocaleString(i18n.language) : '-'), [i18n.language]);

  const dirLabel = useCallback((d) => {
    if (d === 'entry') return t('visit.directionEntry');
    if (d === 'exit') return t('visit.directionExit');
    return '-';
  }, [t]);

  const columns = useMemo(() => [
    { name: 'timestamp', label: t('visit.timestamp'), options: { filter: false, sort: false, customBodyRender: (v) => fmt(v) } },
    { name: 'gateName', label: t('visit.gates'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    { name: 'accessPointName', label: t('visit.accessPoint'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    { name: 'direction', label: t('visit.direction'), options: { filter: false, sort: false, customBodyRender: (v) => dirLabel(v) } },
    {
      name: 'isGranted',
      label: t('visit.status'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, tableMeta) => {
          const row = movements[tableMeta.rowIndex];
          if (!row) return null;
          return v ? (
            <span style={{ color: '#1d9e74', fontWeight: 600 }}>{t('visit.granted')}</span>
          ) : (
            <span style={{ color: '#d64545', fontWeight: 600 }}>
              {t(`responseErrors.${row.denyReason}`, { defaultValue: row.denyReason })}
            </span>
          );
        },
      },
    },
  ], [t, fmt, dirLabel, movements]);

  if (!fetching && !visit) {
    return <Container><Card>{t(`responseErrors.visit.notFound`, { defaultValue: 'visit.notFound' })}</Card></Container>;
  }

  return (
    <Container>
      <InviteLinkDialog
        open={inviteDialog.open}
        link={inviteDialog.link}
        smsSent={inviteDialog.smsSent}
        onClose={() => setInviteDialog({ open: false, link: null, smsSent: false })}
      />

      <ConfirmDialog
        isOpen={cancelConfirm}
        title={t('visit.cancelTitle')}
        message={t('visit.cancelMessage', { name: visit?.visitorFullName || '' })}
        onConfirm={handleCancel}
        onCancel={() => setCancelConfirm(false)}
        loading={busy}
      />

      <Card>
        <TitleRow>
          <h2>{visit?.visitorFullName}</h2>
          {canWrite('VM') && visit?.status === 'approved' && (
            <Actions>
              <Button onClick={() => handleSendInvite(true)}>{t('visit.sendSms')}</Button>
              <Button onClick={() => handleSendInvite(false)}>{t('visit.showLink')}</Button>
              <Button onClick={() => setCancelConfirm(true)}>{t('visit.cancelVisit')}</Button>
            </Actions>
          )}
        </TitleRow>
        <InfoGrid>
          <div><b>{t('visit.visitorPhone')}</b>{visit?.visitorPhone || '-'}</div>
          <div><b>{t('visit.visitorNationalId')}</b>{visit?.visitorNationalId || '-'}</div>
          <div><b>{t('visit.visitorCompany')}</b>{visit?.visitorCompany || '-'}</div>
          <div><b>{t('visit.vehiclePlate')}</b>{visit?.vehiclePlate || '-'}</div>
          <div><b>{t('visit.hostEmployee')}</b>{visit?.hostEmployeeName || '-'}</div>
          <div><b>{t('visit.validFrom')}</b>{fmt(visit?.validFrom)}</div>
          <div><b>{t('visit.validTo')}</b>{fmt(visit?.validTo)}</div>
          <div>
            <b>{t('visit.gates')}</b>
            {(visit?.gates || []).map((g) => g.name).join(', ') || '-'}
          </div>
          {visit?.dailyTimeStart && visit?.dailyTimeEnd && (
            <div><b>{t('guest.dailyHours')}</b>{visit.dailyTimeStart} — {visit.dailyTimeEnd}</div>
          )}
          <div><b>{t('visit.notes')}</b>{visit?.notes || '-'}</div>
        </InfoGrid>
      </Card>

      <DataTable
        title={t('visit.movements')}
        fetching={fetching}
        columns={columns}
        data={movements}
        totalRowCount={movements.length}
        pageIndex={0}
        pageSize={movements.length || 10}
        serverSide={false}
        disableFilters
      />
    </Container>
  );
}
